import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { DEFAULT_FLAG_VALUES, KillSwitchKey } from '@/hooks/useKillSwitch';

interface FeatureFlag {
  id: string;
  flag_key: string;
  enabled: boolean;
  description?: string | null;
  category?: string | null;
  priority?: string | null;
  requires_confirmation?: boolean | null;
}

/**
 * Hook for admin feature flags management
 * Uses React Query for caching and automatic updates
 */
export function useAdminFeatureFlags() {
  const queryClient = useQueryClient();

  const { data: flags = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['kill-switches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_feature_flags')
        .select('*')
        .order('priority', { ascending: false })
        .order('flag_key');

      if (error) {
        console.error('Erreur lors du chargement des feature flags:', error);
        throw error;
      }
      return data as FeatureFlag[];
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const toggleMutation = useMutation({
    mutationFn: async (flagKey: string) => {
      const flag = flags.find(f => f.flag_key === flagKey);
      if (!flag) throw new Error('Flag not found');

      const { error } = await supabase
        .from('admin_feature_flags')
        .update({ enabled: !flag.enabled, updated_at: new Date().toISOString() })
        .eq('flag_key', flagKey);

      if (error) throw error;
      return { flagKey, newValue: !flag.enabled };
    },
    onSuccess: ({ flagKey, newValue }) => {
      queryClient.invalidateQueries({ queryKey: ['kill-switches'] });
      toast({
        title: "Feature flag mis à jour",
        description: `${flagKey} ${newValue ? 'activé' : 'désactivé'}`,
      });
    },
    onError: (error) => {
      console.error('Erreur lors de la mise à jour du flag:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le feature flag",
        variant: "destructive"
      });
    },
  });

  const toggleFlag = (flagKey: string) => {
    toggleMutation.mutate(flagKey);
  };

  const isEnabled = (flagKey: string): boolean => {
    const flag = flags.find(f => f.flag_key === flagKey);
    if (flag) {
      return flag.enabled;
    }

    const defaultValue = DEFAULT_FLAG_VALUES[flagKey as KillSwitchKey];
    return defaultValue ?? false;
  };

  return {
    flags,
    loading,
    toggleFlag,
    isEnabled,
    refetch
  };
}
